import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import { Redirect } from "react-router-dom";
import "../css/Admin_css/admin-customize.css";
import myToast from "../Utils/MyToast";
import MdlSelectSingleStudent from "../dialog/MdlSelectSingleStudent";
import { Button, Form } from "react-bootstrap";
import BackButton from "../ChildComponent/BackButton";

const FeeStructureManager = () => {
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("token"));
  const [Loading, setLoading] = useState(false);
  const [isOpenStudentSelector, setIsOpenStudentSelector] = useState(false);
  const [mStudentDetails, setMStudentDetails] = useState([]);
  const [mStudentFeeDetails, setMStudentFeeDetails] = useState([]);
  const [ENROLLMENT_NO, setENROLLMENT_NO] = useState("");
  const [ADHAAR_NO, setADHAAR_NO] = useState("");
  const [SESSION_ID, setSESSION_ID] = useState("");
  const [SESSION_NAME, setSESSION_NAME] = useState("");
  const [originalTotalFee, setOriginalTotalFee] = useState(0);
  const [adjustedTotalFee, setAdjustedTotalFee] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [selectedFeeTypes, setSelectedFeeTypes] = useState([]);
  const [editableFees, setEditableFees] = useState({});
  const [feewiseFees, setFeewiseFees] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const HandleShow = useCallback(() => {
    setIsOpenStudentSelector((v) => !v);
  }, []);

  const HandleClose = useCallback(() => {
    setIsOpenStudentSelector(false);
  }, []);

  const HandleSelectedStudent = useCallback(
    (student_id, selected_item, session_id, session_name) => {
      setENROLLMENT_NO(student_id);
      setADHAAR_NO(selected_item.adharcard_no || "");
      setSESSION_ID(session_id);
      setSESSION_NAME(session_name);
      setIsOpenStudentSelector(false);
      getStudentFeeDetails({ session_id, enrollment_no: student_id, aadhar_no: selected_item.adharcard_no || "" });
    },
    []
  );

  const getStudentFeeDetails = useCallback((overrides = {}) => {
    setLoading(true);
    setMStudentDetails([]);
    setMStudentFeeDetails([]);
    setOriginalTotalFee(0);
    setAdjustedTotalFee(0);
    setDiscount(0);
    setSelectedFeeTypes([]);
    setEditableFees({});
    setFeewiseFees({});

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const FUNCTION_NAME = "getStudentFeeCard.php?";
    const URL =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      FUNCTION_NAME;

    const params = {
      school_code: SCHOOL_CODE,
      session_id: overrides.session_id ?? SESSION_ID,
      enrollment_no: overrides.enrollment_no ?? ENROLLMENT_NO,
    };

    // Log full URL with params for debugging
    try {
      const queryString = new URLSearchParams(params).toString();
      console.log("FeeStructureManager getStudentFeeCard URL =>", `${URL}${queryString}`);
    } catch (e) {
      console.log("FeeStructureManager params =>", params);
    }

    axios
      .get(URL, {
        params: params,
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          "Access-Control-Allow-Origin": "*",
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          // getStudentFeeCard returns data with student_details and fee_details
          const studentDetails = response.data?.student_details || [];
          const feeDetails = response.data?.fee_details || [];
          
          setMStudentDetails(studentDetails);
          setMStudentFeeDetails(feeDetails);

          // Organize fees by fee type (fee_name)
          const feewise = {};
          const feeTypes = new Set();
          let total = 0;

          feeDetails.forEach((fee) => {
            const feeName = fee.fee_name || "Unknown Fee";
            if (!feewise[feeName]) {
              feewise[feeName] = [];
            }
            feewise[feeName].push(fee);
            feeTypes.add(feeName);
            total += parseFloat(fee.fee_value || 0);

            // Mark as editable if not collected
            const isCollected = fee.payment_status === "Paid" || parseFloat(fee.amount_paid || 0) > 0;
            const feeKey = `${feeName}_${fee.fee_id}_${fee.month || fee.month2 || ""}`;
            setEditableFees((prev) => ({
              ...prev,
              [feeKey]: !isCollected,
            }));
          });

          setFeewiseFees(feewise);
          setSelectedFeeTypes(Array.from(feeTypes));
          setOriginalTotalFee(total);
          setAdjustedTotalFee(total);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log("error>>>>>", error);
        myToast.notify_error("Error fetching fee details");
      });
  }, [ADHAAR_NO, ENROLLMENT_NO, SESSION_ID]);

  const handleTotalFeeChange = (e) => {
    const newTotal = parseFloat(e.target.value) || 0;
    setAdjustedTotalFee(newTotal);
    const newDiscount = originalTotalFee - newTotal;
    setDiscount(newDiscount >= 0 ? newDiscount : 0);

    // Calculate ratio based on editable and selected fees only
    if (newTotal > 0 && originalTotalFee > 0 && selectedFeeTypes.length > 0) {
      // Calculate total of editable and selected fees
      let totalEditableSelected = 0;
      Object.keys(feewiseFees).forEach((feeName) => {
        feewiseFees[feeName].forEach((fee) => {
          const feeKey = `${feeName}_${fee.fee_id}_${fee.month || fee.month2 || ""}`;
          const isEditable = editableFees[feeKey];
          const isSelected = selectedFeeTypes.includes(feeName);
          if (isEditable && isSelected) {
            totalEditableSelected += parseFloat(fee.fee_value || 0);
          }
        });
      });

      // Calculate total of non-editable or non-selected fees (fixed)
      let totalFixed = originalTotalFee - totalEditableSelected;
      
      // New total for editable selected fees
      const newEditableSelectedTotal = newTotal - totalFixed;
      
      if (totalEditableSelected > 0 && newEditableSelectedTotal > 0) {
        const ratio = newEditableSelectedTotal / totalEditableSelected;
        adjustFeesProportionally(ratio);
      }
    }
  };

  const adjustFeesProportionally = (ratio) => {
    const updatedFeewise = { ...feewiseFees };
    const updatedEditable = { ...editableFees };

    // Calculate total of only editable and selected fees
    let totalEditableSelected = 0;
    Object.keys(updatedFeewise).forEach((feeName) => {
      updatedFeewise[feeName].forEach((fee) => {
        const feeKey = `${feeName}_${fee.fee_id}_${fee.month || fee.month2 || ""}`;
        const isEditable = updatedEditable[feeKey];
        const isSelected = selectedFeeTypes.includes(feeName);
        if (isEditable && isSelected) {
          totalEditableSelected += parseFloat(fee.fee_value || 0);
        }
      });
    });

    // Calculate new total for editable selected fees
    const newEditableSelectedTotal = totalEditableSelected * ratio;

    // Adjust proportionally
    Object.keys(updatedFeewise).forEach((feeName) => {
      updatedFeewise[feeName] = updatedFeewise[feeName].map((fee) => {
        const feeKey = `${feeName}_${fee.fee_id}_${fee.month || fee.month2 || ""}`;
        const isEditable = updatedEditable[feeKey];
        const isSelected = selectedFeeTypes.includes(feeName);

        if (isEditable && isSelected && totalEditableSelected > 0) {
          const originalValue = parseFloat(fee.fee_value || 0);
          const proportion = originalValue / totalEditableSelected;
          const adjustedValue = newEditableSelectedTotal * proportion;
          return {
            ...fee,
            adjusted_fee_value: adjustedValue.toFixed(2),
            original_fee_value: originalValue,
          };
        } else if (!isEditable || !isSelected) {
          // Keep original value for non-editable or non-selected fees
          return {
            ...fee,
            adjusted_fee_value: fee.fee_value,
            original_fee_value: parseFloat(fee.fee_value || 0),
          };
        }
        return fee;
      });
    });

    setFeewiseFees(updatedFeewise);
  };

  const handleFeeTypeToggle = (feeType) => {
    setSelectedFeeTypes((prev) => {
      const updated = prev.includes(feeType)
        ? prev.filter((f) => f !== feeType)
        : [...prev, feeType];

      // Recalculate total when fee types change
      setTimeout(() => {
        let newTotal = 0;
        Object.keys(feewiseFees).forEach((feeName) => {
          feewiseFees[feeName].forEach((fee) => {
            const feeKey = `${feeName}_${fee.fee_id}_${fee.month || fee.month2 || ""}`;
            const isEditable = editableFees[feeKey];
            const isSelected = updated.includes(feeName);
            
            if (isEditable && isSelected) {
              newTotal += parseFloat(fee.adjusted_fee_value || fee.fee_value || 0);
            } else {
              newTotal += parseFloat(fee.fee_value || 0);
            }
          });
        });
        setAdjustedTotalFee(newTotal);
        setDiscount(originalTotalFee - newTotal);
      }, 0);

      return updated;
    });
  };

  const handleIndividualFeeChange = (feeName, feeId, month, value) => {
    const feeKey = `${feeName}_${feeId}_${month || ""}`;
    const updatedFeewise = { ...feewiseFees };

    if (updatedFeewise[feeName]) {
      updatedFeewise[feeName] = updatedFeewise[feeName].map((fee) => {
        if (fee.fee_id === feeId && (fee.month === month || fee.month2 === month)) {
          return {
            ...fee,
            adjusted_fee_value: parseFloat(value) || 0,
          };
        }
        return fee;
      });

      // Recalculate total
      let newTotal = 0;
      Object.values(updatedFeewise).forEach((feeList) => {
        feeList.forEach((fee) => {
          newTotal += parseFloat(fee.adjusted_fee_value || fee.fee_value || 0);
        });
      });

      setFeewiseFees(updatedFeewise);
      setAdjustedTotalFee(newTotal);
      setDiscount(originalTotalFee - newTotal);
    }
  };

  const handleSaveFeeStructure = () => {
    // TODO: Implement API call to save fee structure
    myToast.notify_success("Fee structure saved successfully!");
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (Loading) {
    return <MyLoader />;
  }

  const studentInfo = mStudentDetails[0] || {};
  const feeTypes = Object.keys(feewiseFees).sort();

  return (
    <div className="content">
      <div
        className="content-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i className="fa fa-home" aria-hidden="true" />
              <a href="#"> Manage Fee Structure </a>
            </li>
          </ul>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "30px",
          padding: 0,
          overflow: "hidden",
          borderRadius: "14px",
        }}
      >
        {/* Gradient Header */}
        <div
          className="d-flex align-items-center justify-content-between"
          style={{
            background: "linear-gradient(135deg, #0d6efd, #6610f2, #ff7b54)",
            borderRadius: "14px 14px 0 0",
            padding: "14px 20px",
            boxShadow: "0 10px 25px rgba(15, 23, 42, 0.25)",
            color: "#fff",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 40,
                height: 40,
                backgroundColor: "rgba(255,255,255,0.18)",
              }}
            >
              <i className="fa fa-cog" />
            </span>
            <div>
              <h5 className="mb-0" style={{ fontWeight: 600 }}>
                Fee Structure Management
              </h5>
              <small style={{ opacity: 0.9 }}>
                Adjust and manage student fee structures.
              </small>
            </div>
          </div>
        </div>

        <div style={{ padding: "20px 24px 24px 24px" }}>
          {/* Student Selection */}
          {!ENROLLMENT_NO ? (
            <div className="text-center" style={{ padding: "40px 20px" }}>
              <Button
                onClick={HandleShow}
                variant="primary"
                style={{
                  width: "100%",
                  maxWidth: "400px",
                  borderRadius: "10px",
                  background: COLORS.primary_color_shade5,
                  color: "#fff",
                  borderColor: COLORS.primary_color_shade5,
                  padding: "12px",
                  fontSize: "16px",
                }}
              >
                <i className="fa fa-user-plus" style={{ marginRight: "8px" }} />
                Select Student
              </Button>
            </div>
          ) : (
            <>
              {/* Student Info */}
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "20px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div className="row align-items-center">
                  <div className="col-md-2 text-center">
                    <img
                      src={studentInfo.student_img_path || "/default-avatar.png"}
                      alt={studentInfo.stu_name}
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "3px solid #fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                  </div>
                  <div className="col-md-8">
                    <h5 style={{ margin: "0 0 8px 0", fontWeight: 600 }}>
                      {studentInfo.stu_name}
                    </h5>
                    <p style={{ margin: "4px 0", color: "#6b7280", fontSize: "14px" }}>
                      <strong>Enrollment:</strong> {studentInfo.student_enrollment_no} |{" "}
                      <strong>Roll No:</strong> {studentInfo.student_roll_no} |{" "}
                      <strong>Class:</strong> {studentInfo.class_name} |{" "}
                      <strong>Session:</strong> {SESSION_NAME}
                    </p>
                    <p style={{ margin: "4px 0", color: "#6b7280", fontSize: "14px" }}>
                      <strong>Father:</strong> {studentInfo.father_name} |{" "}
                      <strong>Board:</strong> {studentInfo.board_name}
                    </p>
                  </div>
                  <div className="col-md-2 text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setENROLLMENT_NO("");
                        setMStudentDetails([]);
                        setMStudentFeeDetails([]);
                      }}
                    >
                      Change Student
                    </Button>
                  </div>
                </div>
              </div>

              {/* Total Payable Section */}
              <div
                style={{
                  background: "linear-gradient(135deg, #0d6efd, #6610f2)",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "20px",
                  color: "#fff",
                  boxShadow: "0 4px 12px rgba(13, 110, 253, 0.3)",
                }}
              >
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <label style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
                      Original Total Fee
                    </label>
                    <div style={{ fontSize: "24px", fontWeight: 700 }}>
                      ₹{originalTotalFee.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
                      Adjusted Total Payable
                    </label>
                    <Form.Control
                      type="number"
                      value={adjustedTotalFee}
                      onChange={handleTotalFeeChange}
                      style={{
                        fontSize: "20px",
                        fontWeight: 700,
                        background: "rgba(255,255,255,0.2)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        color: "#fff",
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
                      Discount
                    </label>
                    <div style={{ fontSize: "24px", fontWeight: 700, color: "#4ade80" }}>
                      ₹{discount.toFixed(2)}
                    </div>
                    {discount > 0 && (
                      <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
                        ({(discount / originalTotalFee * 100).toFixed(1)}%)
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Fee Type Selection */}
              {selectedFeeTypes.length > 0 && (
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "20px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <h6 style={{ marginBottom: "12px", fontWeight: 600 }}>
                    Select Fee Types to Adjust:
                  </h6>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {selectedFeeTypes.map((feeType) => (
                      <Form.Check
                        key={feeType}
                        type="checkbox"
                        label={feeType}
                        checked={selectedFeeTypes.includes(feeType)}
                        onChange={() => handleFeeTypeToggle(feeType)}
                        style={{ marginRight: "16px" }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Fee-wise Display */}
              {feeTypes.length > 0 ? (
                <div>
                  {feeTypes.map((feeName) => {
                    const feeList = feewiseFees[feeName];
                    const totalForFeeType = feeList.reduce((sum, fee) => {
                      return sum + parseFloat(fee.adjusted_fee_value || fee.fee_value || 0);
                    }, 0);
                    const paidForFeeType = feeList.reduce((sum, fee) => {
                      return sum + parseFloat(fee.amount_paid || 0);
                    }, 0);
                    const outstandingForFeeType = totalForFeeType - paidForFeeType;

                    return (
                      <div
                        key={feeName}
                        style={{
                          background: "#fff",
                          borderRadius: "12px",
                          padding: "20px",
                          marginBottom: "20px",
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "16px",
                            paddingBottom: "12px",
                            borderBottom: "2px solid #e5e7eb",
                          }}
                        >
                          <h5
                            style={{
                              margin: 0,
                              fontWeight: 600,
                              color: COLORS.primary_color_shade5,
                            }}
                          >
                            {feeName}
                          </h5>
                          <div style={{ display: "flex", gap: "20px", fontSize: "14px" }}>
                            <span>
                              <strong>Total:</strong> ₹{totalForFeeType.toFixed(2)}
                            </span>
                            <span>
                              <strong>Paid:</strong> ₹{paidForFeeType.toFixed(2)}
                            </span>
                            <span
                              style={{
                                color: outstandingForFeeType > 0 ? "#dc2626" : "#059669",
                                fontWeight: 600,
                              }}
                            >
                              <strong>Outstanding:</strong> ₹{outstandingForFeeType.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="table" style={{ marginBottom: 0 }}>
                            <thead>
                              <tr style={{ background: "#f8f9fa" }}>
                                <th style={{ padding: "12px", fontWeight: 600 }}>Month</th>
                                <th style={{ padding: "12px", fontWeight: 600 }}>Original Amount</th>
                                <th style={{ padding: "12px", fontWeight: 600 }}>Adjusted Amount</th>
                                <th style={{ padding: "12px", fontWeight: 600 }}>Status</th>
                                <th style={{ padding: "12px", fontWeight: 600 }}>Paid</th>
                                <th style={{ padding: "12px", fontWeight: 600 }}>Outstanding</th>
                              </tr>
                            </thead>
                            <tbody>
                              {feeList.map((fee, idx) => {
                                const month = fee.month || fee.month2 || "N/A";
                                const feeKey = `${feeName}_${fee.fee_id}_${month}`;
                                const isEditable = editableFees[feeKey];
                                const originalValue = parseFloat(fee.fee_value || 0);
                                const adjustedValue = parseFloat(
                                  fee.adjusted_fee_value || fee.fee_value || 0
                                );
                                const paidAmount = parseFloat(fee.amount_paid || 0);
                                const outstanding = adjustedValue - paidAmount;
                                const isPaid = fee.payment_status === "Paid" || paidAmount > 0;

                                return (
                                  <tr key={idx}>
                                    <td style={{ padding: "12px", fontWeight: 500 }}>
                                      {month}
                                    </td>
                                    <td style={{ padding: "12px" }}>₹{originalValue.toFixed(2)}</td>
                                    <td style={{ padding: "12px" }}>
                                      {isEditable ? (
                                        <Form.Control
                                          type="number"
                                          value={adjustedValue}
                                          onChange={(e) =>
                                            handleIndividualFeeChange(
                                              feeName,
                                              fee.fee_id,
                                              month,
                                              e.target.value
                                            )
                                          }
                                          style={{
                                            width: "120px",
                                            display: "inline-block",
                                          }}
                                          disabled={!isEditable}
                                        />
                                      ) : (
                                        <span>₹{adjustedValue.toFixed(2)}</span>
                                      )}
                                    </td>
                                    <td style={{ padding: "12px" }}>
                                      <span
                                        style={{
                                          padding: "4px 12px",
                                          borderRadius: "12px",
                                          fontSize: "12px",
                                          fontWeight: 600,
                                          background: isPaid ? "#d1fae5" : "#fee2e2",
                                          color: isPaid ? "#065f46" : "#991b1b",
                                        }}
                                      >
                                        {isPaid ? "Paid" : "Pending"}
                                      </span>
                                    </td>
                                    <td style={{ padding: "12px" }}>₹{paidAmount.toFixed(2)}</td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        color: outstanding > 0 ? "#dc2626" : "#059669",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ₹{outstanding.toFixed(2)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    color: "#6b7280",
                  }}
                >
                  <i
                    className="fa fa-inbox"
                    style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}
                  />
                  <p style={{ fontSize: "16px", margin: 0 }}>
                    No fee details found for this student.
                  </p>
                </div>
              )}

              {/* Save Button */}
              {feeTypes.length > 0 && (
                <div className="text-center" style={{ marginTop: "30px" }}>
                  <Button
                    onClick={handleSaveFeeStructure}
                    variant="success"
                    size="lg"
                    style={{
                      padding: "12px 40px",
                      fontSize: "18px",
                      fontWeight: 600,
                      borderRadius: "10px",
                    }}
                  >
                    <i className="fa fa-save" style={{ marginRight: "8px" }} />
                    Save Fee Structure
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isOpenStudentSelector && (
        <MdlSelectSingleStudent
          action={HandleClose}
          action2={HandleSelectedStudent}
        />
      )}
    </div>
  );
};

export default FeeStructureManager;

